"use client";

import { Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type ThemeId = "light" | "dark";

function resolveDisplayTheme(
  theme: string | undefined,
  resolvedTheme: string | undefined,
): ThemeId {
  if (theme === "light" || theme === "dark") return theme;
  return resolvedTheme === "dark" ? "dark" : "light";
}

export default function ThemeSwitcher() {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [iconTheme, setIconTheme] = useState<ThemeId>("light");
  const syncedRef = useRef(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!mounted || syncedRef.current) return;
    if (!theme && !resolvedTheme) return;

    setIconTheme(resolveDisplayTheme(theme, resolvedTheme));
    syncedRef.current = true;
  }, [mounted, theme, resolvedTheme]);

  if (!mounted) {
    return <div className="h-11 w-11 shrink-0" aria-hidden />;
  }

  const isDark = iconTheme === "dark";
  const nextTheme: ThemeId = isDark ? "light" : "dark";

  const toggleTheme = () => {
    const next: ThemeId = isDark ? "light" : "dark";
    setIconTheme(next);
    setTheme(next);
  };

  return (
    <button
      type="button"
      aria-label={`Switch to ${nextTheme} mode`}
      onClick={toggleTheme}
      className="focus-ring group relative flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-border/50 bg-background/80 text-foreground shadow-sm hover:border-foreground/30 hover:bg-foreground hover:text-background"
    >
      <div className="absolute inset-0 flex h-full w-full -translate-x-full -skew-x-13 justify-center group-hover:translate-x-full motion-safe:duration-1000">
        <div className="relative h-full w-4 bg-background/20" />
      </div>

      <span className="relative z-10 size-6 overflow-hidden rounded-full">
        <span
          className={cn(
            "flex flex-col gap-8 motion-safe:transition-transform motion-safe:duration-500 motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)]",
            isDark && "-translate-y-[calc(1.5rem+2rem)]",
          )}
        >
          <Sun aria-hidden className="size-6 shrink-0" />
          <Moon aria-hidden className="size-6 shrink-0" />
        </span>
      </span>
    </button>
  );
}
